/**
 * ChargebackDashboardAgGridHook
 *
 * AG Grid hook for the Chargeback Management dashboard's case list.
 * Server-side filtering (status/scheme/priority/MID/date + the quick-queue
 * tabs) stays exactly as it was — this only replaces how the *currently
 * filtered* rows are rendered, not what gets filtered.
 *
 * LiveView events received:
 *  "update_chargeback_grid" – { rows: [...] } – pushed after any handler
 *  that changes the filtered case list (filter, quick_filter, CSV import,
 *  a lifecycle transition, ownership change, new dispute logged, ...).
 *
 * LiveView events pushed:
 *  "view"        – { id: "<id>" } – opens the Case Detail drawer (row click
 *                  or the "Detail" action button).
 *  "open_modal"  – { type: "<...>", id: "<id>" } – same contract the old
 *                  per-row `<button phx-click="open_modal" ...>`s used.
 */

const ChargebackDashboardAgGridHook = {
  mounted() {
    const container = this.el;
    const rows = JSON.parse(container.dataset.rows || "[]");
    const self = this;

    const schemeBadge = (value) => {
      if (!value) return "—";
      const colours = { visa: ["#eff6ff", "#1d4ed8"], mastercard: ["#fff7ed", "#c2410c"] };
      const [bg, fg] = colours[value] || ["#f9fafb", "#4b5563"];
      return `<span style="background:${bg};color:${fg};padding:2px 8px;border-radius:4px;font-size:11px;font-weight:500;">${value}</span>`;
    };

    const statusBadge = (value) => {
      const colours = {
        dispute:         ["#f3e8ff", "#7e22ce"],
        received:        ["#fef9c3", "#a16207"],
        representment:   ["#dbeafe", "#1d4ed8"],
        pre_arbitration: ["#ffedd5", "#c2410c"],
        arbitration:     ["#fee2e2", "#b91c1c"],
        resolved:        ["#dcfce7", "#15803d"],
        written_off:     ["#f3f4f6", "#4b5563"],
      };
      const [bg, fg] = colours[value] || ["#f3f4f6", "#4b5563"];
      return `<span style="background:${bg};color:${fg};padding:2px 8px;border-radius:4px;font-size:11px;font-weight:600;">${value || ""}</span>`;
    };

    const priorityBadge = (params) => {
      const colours = {
        critical: ["#fee2e2", "#b91c1c"],
        high:     ["#ffedd5", "#c2410c"],
        low:      ["#f3f4f6", "#6b7280"],
      };
      const [bg, fg] = colours[params.value] || ["#f9fafb", "#4b5563"];
      const esc = params.data.escalated
        ? ` <span style="background:#fee2e2;color:#b91c1c;padding:1px 4px;border-radius:3px;font-size:10px;">ESC</span>`
        : "";
      return `<span style="background:${bg};color:${fg};padding:2px 8px;border-radius:4px;font-size:11px;">${params.value || ""}</span>${esc}`;
    };

    const dueDateCell = (params) => {
      if (!params.value) return "—";
      const badge = params.data.past_due
        ? ` <span style="background:#fee2e2;color:#b91c1c;padding:1px 4px;border-radius:3px;font-size:10px;">PAST DUE</span>`
        : "";
      const style = params.data.past_due ? "color:#dc2626;font-weight:600;" : "color:#6b7280;";
      return `<span style="${style}">${params.value}</span>${badge}`;
    };

    // ── Actions cell renderer — same status-driven button set the old
    // per-row <table> had, just built as DOM nodes instead of EEx conditionals.
    const actionsRenderer = (params) => {
      if (!params.data) return "";
      const { id, status } = params.data;
      const wrap = document.createElement("div");
      wrap.style.cssText = "display:flex;align-items:center;gap:8px;flex-wrap:wrap;padding:2px 0;";

      const btn = (label, color, onClick) => {
        const el = document.createElement("button");
        el.textContent = label;
        el.style.cssText = `color:${color};font-size:11px;cursor:pointer;background:none;border:none;padding:0;white-space:nowrap;text-decoration:underline;`;
        el.addEventListener("click", (e) => { e.stopPropagation(); onClick(); });
        return el;
      };

      wrap.appendChild(btn("Detail", "#4f46e5", () => self.pushEvent("view", { id: String(id) })));

      if (status === "dispute") {
        wrap.appendChild(btn("File as Chargeback", "#7e22ce", () =>
          self.pushEvent("open_modal", { type: "file_as_chargeback", id: String(id) })));
      }
      if (status === "received" || status === "pre_arbitration") {
        wrap.appendChild(btn("Represent", "#2563eb", () =>
          self.pushEvent("open_modal", { type: "representment", id: String(id) })));
      }
      if (status === "representment") {
        wrap.appendChild(btn("Pre-Arb", "#c2410c", () =>
          self.pushEvent("open_modal", { type: "pre_arbitration", id: String(id) })));
      }
      if (status === "pre_arbitration") {
        wrap.appendChild(btn("Arbitration", "#dc2626", () =>
          self.pushEvent("open_modal", { type: "arbitration", id: String(id) })));
      }
      if (!["dispute", "resolved", "written_off"].includes(status)) {
        wrap.appendChild(btn("Resolve", "#16a34a", () =>
          self.pushEvent("open_modal", { type: "resolve", id: String(id) })));
        wrap.appendChild(btn("Write Off", "#6b7280", () =>
          self.pushEvent("open_modal", { type: "write_off", id: String(id) })));
      }

      return wrap;
    };

    const columnDefs = [
      { headerName: "Case #", field: "case_number", width: 170, pinned: "left", cellClass: "font-mono" },
      { headerName: "MID", field: "merchant_mid", width: 130, cellClass: "font-mono" },
      { headerName: "Scheme", field: "scheme_name", width: 110, cellRenderer: (p) => schemeBadge(p.value) },
      { headerName: "RRN", field: "rrn", width: 150, cellClass: "font-mono" },
      { headerName: "Txn Amount", field: "transaction_amount", width: 120, type: "numericColumn", cellClass: "font-mono" },
      {
        headerName: "Reason",
        field: "chargeback_reason_code",
        width: 160,
        tooltipField: "chargeback_reason_description",
        valueFormatter: (p) => p.value || "",
      },
      { headerName: "CB Date", field: "chargeback_date", width: 110, cellClass: "font-mono" },
      { headerName: "Due Date", field: "response_due_date", width: 150, cellRenderer: dueDateCell },
      { headerName: "Status", field: "status", width: 130, cellRenderer: (p) => statusBadge(p.value) },
      { headerName: "Priority", field: "priority", width: 130, cellRenderer: priorityBadge },
      { headerName: "Assigned", field: "assigned_to_name", width: 140 },
      { headerName: "Recovery", field: "recovery_amount", width: 110, type: "numericColumn", cellClass: "font-mono", valueFormatter: (p) => p.value || "—" },
      {
        headerName: "Actions",
        colId: "actions",
        pinned: "right",
        width: 260,
        sortable: false,
        filter: false,
        floatingFilter: false,
        resizable: false,
        cellRenderer: actionsRenderer,
      },
    ];

    // No client-side `pagination` option here on purpose: `rows` is already
    // just the current server-fetched page (see Context.list_chargebacks_page/3
    // and the Prev/Next controls in the LiveView template below this grid) —
    // a second, AG-Grid-local pager over that single page would just be
    // confusing, not an actual second page of data.
    const gridOptions = {
      columnDefs,
      rowData: rows,
      defaultColDef: { resizable: true, sortable: true, filter: true, floatingFilter: true },
      animateRows: true,
      rowSelection: "single",
      onRowClicked: (event) => {
        if (event.data && event.data.id && event.column && event.column.getColId() !== "actions") {
          self.pushEvent("view", { id: String(event.data.id) });
        }
      },
      overlayNoRowsTemplate: `<span style="padding:20px;color:#9ca3af;">No chargeback cases found.</span>`,
    };

    if (window.agGrid && window.agGrid.createGrid) {
      this.gridApi = window.agGrid.createGrid(container, gridOptions);
    } else {
      console.error("ChargebackDashboardAgGridHook: window.agGrid not found");
    }

    this.handleEvent("update_chargeback_grid", (payload) => {
      if (!payload || !Array.isArray(payload.rows) || !this.gridApi) return;
      this.gridApi.setGridOption("rowData", payload.rows);
    });
  },

  destroyed() {
    if (this.gridApi) {
      this.gridApi.destroy();
      this.gridApi = null;
    }
  },
};

export default ChargebackDashboardAgGridHook;
